import { useNavigate, useLocation, useParams } from "react-router-dom";
import React, { useState, useEffect, useContext } from "react";

import { UserContext } from "./UserContext.js";
import styles from "./ScanOrderScreen.module.css";

import Button from "./components/Button.js";
import QRScanner from "./components/QRCodeScanner.js";

import greenCircles from "./assets/images/greenCirclesCorner.png";
import bbbLogo from "./assets/images/bbbLogo.png";
import successImage from "./assets/images/check.png";
import iconShoppingBag from "./assets/images/iconShoppingBag.png";

const ScanOrderScreen = (props) => {
  document.title = "Scan Bags";

  const { orderId } = useParams();
  const { state: navState } = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const backEndURL = props.backEndURL || user?.backEndURL;

  const {
    selectedOrder: selectedOrderFromState,
    orders = [],
    totalBagsExpected: totalBagsExpectedFromState = "?",
    location: navLocation = { lat: null, long: null },
    charcodeId,
    initialScreen = "scanner",
  } = navState || {};

  const [selectedOrder, setSelectedOrder] = useState(selectedOrderFromState || null);
  const [loadedOrder, setLoadedOrder] = useState(null);
  const [totalBagsExpected, setTotalBagsExpected] = useState(totalBagsExpectedFromState);
  const [location, setLocation] = useState(navLocation);
  const [ordersContent, setOrdersContent] = useState([]);
  const [errorPopup, setErrorPopup] = useState(null);
  const [isRemovingBag, setIsRemovingBag] = useState(false);
  const [screen, setScreen] = useState(initialScreen);
  const [lastAddedCharcode, setLastAddedCharcode] = useState(null);
  const [bagAddedToast, setBagAddedToast] = useState(false);

  useEffect(() => {
    if (!orderId) navigate(-1);
  }, [navigate, orderId]);

  useEffect(() => {
    if (!backEndURL || (!selectedOrder && !orderId)) return;

    let cancelled = false;
    const refreshOrderBags = async () => {
      try {
        const response = await fetch(
          `${backEndURL}/order/scan/${encodeURIComponent(selectedOrder || orderId)}`
        );
        const json = await response.json();
        if (!cancelled && response.ok && json.success) {
          setSelectedOrder(json.data.orderId);
          setLoadedOrder(json.data.order || null);
          setTotalBagsExpected(json.data.totalBagsExpected);
          setOrdersContent(
            (json.data?.bags || []).map((bag) => ({
              order_id: json.data.orderId,
              charcode: bag.charcode,
            }))
          );
        }
      } catch (err) {
        console.error("refreshOrderBags error:", err);
      }
    };

    refreshOrderBags();
    return () => {
      cancelled = true;
    };
  }, [backEndURL, orderId, selectedOrder]);

  const getLocation = () => {
    if (location && location.lat !== null) {
      return Promise.resolve(location);
    }
    if (process.env.NODE_ENV === 'development') {
      const coords = { lat: 51.5074, long: -0.1278 };
      setLocation(coords);
      return Promise.resolve(coords);
    }
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = { lat: position.coords.latitude, long: position.coords.longitude };
            setLocation(coords);
            resolve(coords);
          },
          (error) => {
            console.log(error);
            alert("Location access was denied. Please allow location access and try again.");
            reject(error);
          }
        );
      } else {
        alert("Geolocation is not supported by this browser.");
        reject(new Error("Geolocation not supported"));
      }
    });
  };

  const addBagToOrderFromScanner = async (charcode, overrideSiteCheck = false) => {
    let currentLocation;
    try {
      currentLocation = await getLocation();
    } catch {
      return;
    }

    const currentOrder = orders.find(o => String(o._id) === String(selectedOrder));
    let deliverToStorage = false;
    let deliverToProcessing = false;
    if (currentOrder && currentOrder.destination_type === "site") {
      const destCat = currentOrder._destination_site?.category?.toLowerCase() || "";
      if (destCat === "processing") deliverToProcessing = true;
      else deliverToStorage = true;
    }
    const requestBody = {
      bagsToUpdate: [{ charcode }],
      newStatus: "assigned",
      selectedOrder,
      deliverToStorage,
      deliverToProcessing,
      location: currentLocation,
      overrideSiteCheck,
    };

    try {
      const response = await fetch(backEndURL + "/charcodes", {
        method: "put",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
      const json = await response.json();

      if (json.success) {
        const newCount = allBags.length + 1;
        setOrdersContent(prev => [...prev, { order_id: selectedOrder, charcode }]);
        setLastAddedCharcode(charcode);
        const expectedCount = Number(totalBagsExpected);
        if (Number.isFinite(expectedCount) && newCount >= expectedCount) {
          setScreen("success");
        } else {
          setBagAddedToast(true);
          setTimeout(() => setBagAddedToast(false), 500);
        }
      } else {
        const errorType = json.errorType;

        if (errorType === "WRONG_SITE") {
          setErrorPopup({
            title: "Location mismatch",
            body: `Bag ${json.charcode} is at ${json.bagSiteName}, but this order picks up from ${json.expectedSiteName}. Would you like to add the bag anyway?`,
            details: [],
            onConfirm: () => addBagToOrderFromScanner(charcode, true),
          });
        } else if (errorType === "NOT_EBC_CERTIFIED") {
          setErrorPopup({
            title: "Bag not EBC certified",
            body: `Bag ${json.charcode} is not EBC certified and cannot be added to this order. Remove EBC certification from the label if not already done.`,
            details: [],
          });
        } else if (errorType === "TOO_RECENT") {
          const parsedDate = json.baggingDate ? new Date(json.baggingDate) : null;
          const dateStr = parsedDate && !Number.isNaN(parsedDate.getTime())
            ? parsedDate.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
            : "Unknown date";
          setErrorPopup({
            title: "Bag produced less than 5 days ago",
            body: `Bag ${json.charcode} was bagged on ${dateStr} and must wait 5 days before it can be picked up.`,
            details: ["Remove this bag from the list."],
          });
        } else {
          setErrorPopup({
            title: "Failed to add bag",
            body: json.message ?? "An unexpected error occurred.",
            details: [],
          });
        }
      }
    } catch (err) {
      console.error("addBagToOrderFromScanner error:", err);
      setErrorPopup({
        title: "Network error",
        body: "Could not connect to server.",
        details: [],
      });
    }
  };

  const allBags = ordersContent.filter(item => String(item.order_id) === String(selectedOrder));
  const currentOrder =
    orders.find(order => String(order._id) === String(selectedOrder)) ||
    loadedOrder;
  const orderDisplayId = currentOrder?.order_id || orderId;
  const expectedBagCount = Number(totalBagsExpected);
  const orderFull =
    Number.isFinite(expectedBagCount) &&
    allBags.length === expectedBagCount;

  const openFinaliseShipment = () => {
    navigate(`/order/${orderDisplayId}`, {
      state: { sessionBags: allBags.length },
    });
  };

  const removeBagFromOrder = async (charcode) => {
    if (isRemovingBag) return;
    setIsRemovingBag(true);

    try {
      const response = await fetch(backEndURL + "/charcodes", {
        method: "put",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          bagsToUpdate: [{ charcode }],
          newStatus: "removedFromOrder",
          selectedOrder,
        }),
      });
      const json = await response.json();

      if (!response.ok || !json.success) {
        setErrorPopup({
          title: "Failed to remove bag",
          body: json.message ?? `Could not remove ${charcode} from order ${orderDisplayId}.`,
          details: [],
        });
        return;
      }

      setOrdersContent(prev =>
        prev.filter(b => !(String(b.order_id) === String(selectedOrder) && b.charcode === charcode))
      );
      setErrorPopup(null);
    } catch (err) {
      console.error("removeBagFromOrder error:", err);
      setErrorPopup({
        title: "Network error",
        body: `Could not remove ${charcode} from order ${orderDisplayId}.`,
        details: [],
      });
    } finally {
      setIsRemovingBag(false);
    }
  };

  const confirmBagRemoval = (item) => {
    const charcode = typeof item === "string" ? item : item.charcode;
    setErrorPopup({
      title: "Remove bag?",
      body: `Are you sure you want to remove charcode "${charcode}" from order "${orderDisplayId}"?`,
      details: [],
      confirmLabel: "Yes",
      cancelLabel: "No",
      closeOnConfirm: false,
      onConfirm: () => removeBagFromOrder(charcode),
    });
  };

  const handleClose = () => {
    setScreen("orderView");
  };

  const renderContent = () => {
    if (screen === "success") {
      return (
        <div className={styles.contentScreen}>
          <img src={successImage} className={styles.mainIllustration} alt="Success"/>
          <div className={styles.header}>Bag Added!</div>
          <div className={styles.description}>
            {lastAddedCharcode} was added to order {orderDisplayId}.
          </div>
          <div className={styles.ghostSpace}/>
          <Button
            color="Grass"
            name="View Order"
            onPress={() => setScreen("orderView")}
            customStyle={{ width: "100%", marginBottom: "8px" }}
          />
          <Button
            color="Grass"
            name={orderFull ? "Finalise Shipment" : "Add More Bags"}
            onPress={orderFull ? openFinaliseShipment : () => setScreen("scanner")}
            customStyle={{ width: "100%" }}
          />
        </div>
      );
    }

    if (screen === "orderView") {
      return (
        <div className={`${styles.contentScreen} ${styles.orderViewContent}`}>
          <div className={styles.header}>Order {orderDisplayId}</div>
          <div className={styles.description}>Bags loaded for this order</div>
          <div className={styles.sectionHeader}>
            <div>
              <img src={iconShoppingBag} className={styles.sectionIcon} alt=""/>
              {" "}Bags ({allBags.length} / {totalBagsExpected})
            </div>
          </div>
          <div className={styles.charcodesContainer}>
            {allBags.map((bag) => (
              <button
                type="button"
                key={bag.charcode}
                className={styles.headerCharcode}
                onClick={() => confirmBagRemoval(bag.charcode)}
              >
                <span>{bag.charcode}</span>
                <div className={styles.backspaceIcon}>backspace</div>
              </button>
            ))}
          </div>
          <Button
            color="Grass"
            name={orderFull ? "Finalise Shipment" : "Add More Bags"}
            onPress={orderFull ? openFinaliseShipment : () => setScreen("scanner")}
            customStyle={{ width: "100%" }}
          />
        </div>
      );
    }

    return (
      <QRScanner
        scanResults={allBags.map(b => b.charcode)}
        totalBagsExpected={totalBagsExpected}
        addCharcode={addBagToOrderFromScanner}
        removeCharcode={confirmBagRemoval}
        updateScannedCharcodes={() => {}}
        closeScanner={handleClose}
        closeScannerLabel="View Order"
        backEndURL={backEndURL}
      />
    );
  };

  return (
    <div className={styles.fullContainer}>
      <div className={styles.patternBg}/>

      <div className={styles.blackContainerWrapper}>
        <div className={styles.logoContainer}>
          <img src={bbbLogo} className={styles.logo}/>
        </div>
        <div className={styles.blackContainer}>
          <div className={styles.container}>
            <img src={greenCircles} className={styles.circle1}/>
            <img src={greenCircles} className={styles.circle2}/>

            {renderContent()}

            {bagAddedToast && (
              <div className={styles.bagAddedToast}>Bag Added!</div>
            )}

            {errorPopup && (
              <div className={styles.errorPopupOverlay}>
                <div className={styles.errorPopupCard}>
                  <div className={styles.errorPopupTitle}>{errorPopup.title}</div>
                  <div className={styles.errorPopupBody}>{errorPopup.body}</div>
                  {errorPopup.details?.length > 0 && (
                    <ul className={styles.errorPopupDetails}>
                      {errorPopup.details.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  )}
                  {errorPopup.onConfirm ? (
                    <>
                      <Button
                        name={errorPopup.confirmLabel || "Yes, add anyway"}
                        disabled={isRemovingBag}
                        onPress={() => {
                          const onConfirm = errorPopup.onConfirm;
                          if (errorPopup.closeOnConfirm !== false) {
                            setErrorPopup(null);
                          }
                          onConfirm();
                        }}
                        color="Grass"
                        customStyle={{ marginTop: "12px", width: "100%", marginBottom: "6px" }}
                      />
                      <Button
                        name={errorPopup.cancelLabel || "No"}
                        disabled={isRemovingBag}
                        onPress={() => setErrorPopup(null)}
                        color="Coal"
                        customStyle={{ width: "100%" }}
                      />
                    </>
                  ) : (
                    <Button
                      name="OK"
                      onPress={() => setErrorPopup(null)}
                      color="Grass"
                      customStyle={{ marginTop: "12px", width: "100%" }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        className={styles.websiteLink}
        onClick={() => window.open("https://blackbullbiochar.com", "_blank")?.focus()}
      >
        <span className={styles.fontAwesome}>globe</span>
        BLACKBULLBIOCHAR.COM
      </button>
    </div>
  );
};

export default ScanOrderScreen;
